import { useMemo, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { SlideOut } from '@interstate/components/SlideOut';
import type { PrimaryButton, OptionButton, OptionButtonTuple } from '@interstate/components/ActionButtons';
import { Box } from '@interstate/components/Box';
import { ChevronLeftIcon } from '@interstate/components/Icons/ChevronLeftIcon';
import { Action } from '@interstate/components/Action';
import { Typography } from '@interstate/components/Typography';
import { FINANCE, LEASE, BALLOON } from '@makemydeal/dr-platform-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { ProgramsFiltersSlideOutProps, ProgramFilters } from './ProgramsFilters.types';
import { getVisibleFilterConfigs, FILTERS_PANEL_WIDTH, FILTERS_WIDE_PANEL_WIDTH } from './ProgramsFilters.types';
import { FilterSection } from './FilterSection';
import { toggleFilterValue, setFilterValue } from './ProgramsFilters.utils';
import { FilterDivider } from './styles';

export const ProgramsFiltersSlideOut = ({
    isOpen,
    onClose,
    hasNoPrograms,
    filterOptions,
    filters,
    onFiltersChange,
    onApplyFilters,
    offerType
}: ProgramsFiltersSlideOutProps) => {
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const isPIScreenEnhancementsEnabled = useSelector(featureToggleSelectors.isPIScreenEnhancementsEnabled);
    const isFinanceOnly = offerType === FINANCE && !isFlexBuyPayment;
    const isNonCashOfferType = offerType === FINANCE || offerType === LEASE || offerType === BALLOON;
    const isReadOnlyMode = !isNonCashOfferType || hasNoPrograms;

    const visibleFilters = useMemo(
        () =>
            getVisibleFilterConfigs(!isFinanceOnly, isPIScreenEnhancementsEnabled).filter(
                (config) => !isReadOnlyMode || !config.hideWhenReadOnly
            ),
        [isFinanceOnly, isPIScreenEnhancementsEnabled, isReadOnlyMode]
    );

    const hasWideSection =
        !isReadOnlyMode && visibleFilters.some((config) => config.requiresWidePanel && filterOptions[config.filterKey].length > 0);

    // Handles checkbox toggle for a specific filter category
    const handleFilterChange = useCallback(
        (filterKey: keyof ProgramFilters, useRadioButtons?: boolean) => (value: string) => {
            onFiltersChange({
                ...filters,
                [filterKey]: useRadioButtons ? setFilterValue(value) : toggleFilterValue(filters[filterKey], value)
            });
        },
        [filters, onFiltersChange]
    );

    // Applies filters and closes the slideout
    const handleApply = useCallback(() => {
        onApplyFilters();
        onClose();
    }, [onApplyFilters, onClose]);

    // Cancels filter changes and closes the slideout
    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const primaryButton: PrimaryButton = {
        buttonStyle: 'primary',
        action: handleApply,
        label: 'Apply',
        disabled: isReadOnlyMode
    };

    const optionButtons: OptionButtonTuple = [
        {
            buttonStyle: 'secondary',
            action: handleCancel,
            label: 'Cancel'
        } as OptionButton
    ];

    const filterSlideOutHeader = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Action
                size="sm"
                actionIcon={{ icon: <ChevronLeftIcon />, start: true }}
                onClick={onClose}
                data-testid="programs-filters-back-button"
                sx={{ padding: 0 }}
            >
                Programs and Incentives
            </Action>
        </Box>
    );

    return (
        <SlideOut
            show={isOpen}
            header={filterSlideOutHeader}
            data-testid="programs-filters-slide-out"
            position="right"
            panelWidth={hasWideSection ? FILTERS_WIDE_PANEL_WIDTH : FILTERS_PANEL_WIDTH}
            onHide={onClose}
            footer={{
                primary: primaryButton,
                options: optionButtons
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1rem 0' }}>
                <Typography variant="h4" data-testid="filters-title">
                    Filters
                </Typography>

                {visibleFilters.map((config, index) => {
                    const options = isReadOnlyMode ? [] : filterOptions[config.filterKey];
                    if (!isReadOnlyMode && options.length === 0) return null;

                    const isFirstFilter = index === 0;
                    const useGridLayout =
                        isPIScreenEnhancementsEnabled && config.filterKey === 'ratePrograms' ? true : config.useGridLayout;

                    return (
                        <Fragment key={config.filterKey}>
                            {!isFirstFilter && <FilterDivider data-testid={`filter-divider-${config.filterKey}`} />}
                            <FilterSection
                                filterKey={config.filterKey}
                                label={config.label}
                                options={options}
                                selectedValues={isReadOnlyMode ? [] : filters[config.filterKey]}
                                onChange={handleFilterChange(config.filterKey, config.useRadioButtons)}
                                useGridLayout={useGridLayout}
                                useRadioButtons={config.useRadioButtons}
                                testIdPrefix={config.testIdPrefix}
                                readOnly={isReadOnlyMode}
                                useEnhancedTypography={config.useEnhancedTypography}
                            />
                        </Fragment>
                    );
                })}
            </Box>
        </SlideOut>
    );
};
